import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CircleCheck } from "lucide-react";
import Header from "@/components/Header";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { FaqSection } from "@/components/FaqSection";
import { useAuth } from "@/context/AuthContext";
import seo from "@/seo/pages.json";

export const SEO_LANDINGS = ["/convert-ai-logo-to-vector", "/lost-logo-file-to-illustrator", "/high-resolution-logo-for-print", "/sketch-to-logo"];

const cta = "inline-flex items-center gap-2 bg-[#ED5A24] hover:bg-[#0A0A0B] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-8 transition-colors duration-200";

const Section = ({ s }) => (
  <div className="mb-12">
    <h2 className="text-2xl md:text-3xl tracking-tight font-bold mb-4">{s.h2}</h2>
    {(s.p || []).map((p) => <p key={p} className="text-[#52525B] leading-relaxed mb-4">{p}</p>)}
    {s.steps && (
      <ol className="space-y-3">
        {s.steps.map((t, i) => (
          <li key={t} className="flex gap-4"><span className="font-black text-[#ED5A24] w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span><span>{t}</span></li>
        ))}
      </ol>
    )}
  </div>
);

const Related = ({ current }) => (
  <div className="border-t border-black/10 pt-8" data-testid="seo-related">
    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-4">Also helpful</div>
    <div className="flex flex-wrap gap-3">
      {SEO_LANDINGS.filter((r) => r !== current).map((r) => (
        <Link key={r} to={r} className="border border-black/15 hover:border-[#ED5A24] hover:text-[#ED5A24] px-4 py-2 text-sm transition-colors duration-200">{seo.pages[r].eyebrow}</Link>
      ))}
    </div>
  </div>
);

export default function SeoLanding() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const route = pathname.replace(/(.)\/+$/, "$1");
  const page = seo.pages[route];
  const cta2 = page.cta || {};
  const slider = page.slider || { before: "/sample-ai-before.png", after: "/sample-ai-after.png", beforeLabel: "Low-res", afterLabel: "Vector" };
  const target = cta2.path || "/dashboard";
  const start = user ? target : `/register?next=${encodeURIComponent(target)}`;
  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]" data-testid="seo-landing">
      <Header />
      <section className="border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#ED5A24] mb-4">{page.eyebrow}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.02] mb-6" data-testid="seo-h1">{page.h1}</h1>
            {page.intro.map((p) => <p key={p} className="text-base sm:text-lg text-[#52525B] leading-relaxed mb-4 max-w-2xl">{p}</p>)}
            <Link to={start} className={`${cta} mt-4`} data-testid="seo-cta">{cta2.label || "Upload your logo — preview free"} <ArrowRight className="w-4 h-4" /></Link>
            <div className="flex items-center gap-2 text-sm text-[#52525B] mt-4"><CircleCheck className="w-4 h-4 text-[#ED5A24]" /> {cta2.note || "A$10 per logo · GST included · no subscription"}</div>
          </div>
          <div className="md:col-span-5 border border-black/10">
            <BeforeAfterSlider beforeSrc={slider.before} afterSrc={slider.after} beforeLabel={slider.beforeLabel} afterLabel={slider.afterLabel} bgChecker={false} />
          </div>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        {page.sections.map((s) => <Section key={s.h2} s={s} />)}
        <Related current={route} />
      </section>
      <FaqSection items={page.faq} eyebrow={page.eyebrow} />
      <section className="bg-[#0A0A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 flex flex-wrap items-center justify-between gap-6">
          <h2 className="text-2xl md:text-3xl tracking-tight font-bold">{cta2.bottom || "Ready to fix your logo?"}</h2>
          <Link to={start} className={cta} data-testid="seo-bottom-cta">{cta2.path ? cta2.label : "Start free"} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
}
